import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Envelope, CircleNotch, PaperPlaneTilt } from "@phosphor-icons/react";

export interface EmailInvitePickerProps {
  companyId: string;
  onAdded: () => void;
  onCancel: () => void;
}

export function EmailInvitePicker({
  companyId,
  onAdded,
  onCancel,
}: EmailInvitePickerProps) {
  const [email, setEmail] = useState("");
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  const inviteMutation = useMutation({
    mutationFn: async (inviteEmail: string) => {
      const res = await fetch(
        `/api/companies/${companyId}/supervisors/invite`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email: inviteEmail }),
        }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || "Greška");
      return data;
    },
    onSuccess: (data) => {
      if (data.status === "added") {
        onAdded();
      } else {
        setSuccessMsg("Pozivnica je poslana na email.");
        setEmail("");
      }
    },
  });

  return (
    <div className="space-y-3">
      <p className="text-xs text-muted-foreground">
        Unesite email adresu supervizora. Ako korisnik postoji na platformi,
        biće automatski dodan. Ako ne, potrebna je registracija.
      </p>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (email.trim()) inviteMutation.mutate(email.trim());
        }}
        className="flex gap-2"
      >
        <div className="relative min-w-0 flex-1">
          <Envelope className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            type="email"
            placeholder="email@firma.ba"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setSuccessMsg(null);
            }}
            className="pl-10"
            required
            autoFocus
          />
        </div>
        <Button
          type="submit"
          size="sm"
          disabled={inviteMutation.isPending || !email.trim()}
        >
          {inviteMutation.isPending ? (
            <CircleNotch className="size-4 animate-spin" />
          ) : (
            <PaperPlaneTilt className="size-4" />
          )}
        </Button>
      </form>

      {inviteMutation.error && (
        <p className="text-xs text-rose-600">
          {inviteMutation.error.message}
        </p>
      )}

      {successMsg && (
        <p className="text-xs text-emerald-600">{successMsg}</p>
      )}

      <div className="flex justify-end">
        <Button size="sm" variant="ghost" onClick={onCancel}>
          Otkaži
        </Button>
      </div>
    </div>
  );
}
